/**
 * Patient-app feature flags (rules.md §44, ADR-008, FR-BOOK-02).
 *
 * The M5 parity build reproduces the live heyallergy.com funnel exactly, including its
 * bugs. Every deliberate departure from the live rendering is gated here and defaults
 * to OFF, so each improvement can be turned on, screenshotted before/after and measured
 * (FR-BOOK-02).
 *
 * As of 2026-09-16 the PARITY BUILD IS THE "ALL FLAGS OFF" BUILD rather than the default
 * one: Q-P15 promoted `funnel.a11yContrast` to a default-on flag. The harness asks for
 * that build explicitly (`?flags=none`) and the threshold gate is unchanged.
 *
 * HOW A FLAG IS RESOLVED
 * ----------------------
 * Three layers, each overriding the one above it:
 *
 *   1. `FLAG_DEFAULTS` — every flag OFF except `funnel.a11yContrast`, which Q-P15 turned
 *      on on 2026-09-16. This is what a production bundle renders.
 *   2. The tenant's `features` from `GET /public/tenant` (M5.7). Not wired yet; the
 *      typed seam is `setTenantFeatures()` below, and it is the only thing M5.7 has to
 *      call. TODO(M5.7).
 *   3. `?flags=` on the URL — a client-side override for demos and for the evidence
 *      capture in `apps/patient/parity/compare.mjs --flags`.
 *
 * Layer 3 is honoured while no tenant feature set has been loaded (today: always) or
 * when that set says `flagOverrides: true`, so a production tenant that omits the field
 * silently ignores `?flags=`. These flags change presentation only: nothing here grants
 * access to anything, and authorization never consults them (rules §44.3).
 *
 * WHY THIS IS NOT A PROVIDER
 * --------------------------
 * The whole funnel reads the same process-wide answer, and the answer changes at most
 * once per page load (when the tenant arrives). A module store with
 * `useSyncExternalStore` gives every component the re-render it needs without a context
 * boundary, and lets `readFlags()` serve the handful of non-React call sites.
 *
 * CLEANUP (rules §44.4) — each flag's exit condition is `FLAG_REGISTRY[key].cleanup`,
 * and the rollout plan for the eleven M5–M9 flags is `Docs/_parity/improvements/README.md`.
 * The twelfth, `funnel.insuranceType` (M15), is not a rollout experiment: the tenant turns
 * it on by publishing scheduling rules (the API lists it in `features.ui`), and it is
 * documented in `Docs/BOOKING_FUNNEL_WIRING.md` §3.11.
 */

import { useSyncExternalStore } from 'react';

/* ------------------------------------------------------------------ *
 * The registry
 * ------------------------------------------------------------------ */

/** Every funnel flag, in the order `Docs/_parity/improvements/README.md` lists them. */
export const FUNNEL_FLAG_NAMES = [
  'fontFix',
  'a11yContrast',
  'stepsUnified',
  'fewerSteps',
  'timezoneClarity',
  'mobilePolish',
  'resumePrompt',
  'loadingSkeletons',
  'insuranceCard',
  'smsConsent',
  'marketingConsent',
  'insuranceType',
] as const;

export type FunnelFlagName = (typeof FUNNEL_FLAG_NAMES)[number];

/** The dotted name a flag is known by on the URL and in the docs: `funnel.fontFix`. */
export type FlagKey = `funnel.${FunnelFlagName}`;

export type FunnelFlags = Record<FunnelFlagName, boolean>;

export interface Flags {
  funnel: FunnelFlags;
}

export interface FlagMeta {
  key: FlagKey;
  /** One line: what turning it on changes. */
  summary: string;
  /** The FR-BOOK-02 funnel metric the change is meant to move. */
  metric: string;
  /** rules §44.4 — when the flag and its branch get deleted. */
  cleanup: string;
}

/**
 * The catalogue. Every entry is a temporary flag: none of them is a kill switch, so every
 * one has a deletion condition (rules §44.4, §44.5). `funnel.insuranceType` is the one
 * that follows a tenant setting (a published scheduling ruleset) rather than a rollout.
 */
export const FLAG_REGISTRY: Record<FlagKey, FlagMeta> = {
  'funnel.fontFix': {
    key: 'funnel.fontFix',
    summary:
      'DM Sans on every funnel page. Live, only /payment links the webfont, so the ' +
      'funnel changes typeface mid-flow.',
    metric:
      'Step-to-step drop-off on /book and /insurance (a mid-flow typeface change reads as a different site).',
    cleanup:
      'Delete the flag, the `data-font` attribute and the `[data-font="system"]` rule ' +
      'once the owner signs off on the before/after pair; DM Sans then becomes the only ' +
      'funnel typeface.',
  },
  'funnel.a11yContrast': {
    key: 'funnel.a11yContrast',
    summary:
      'Text, link, step and button colours that clear WCAG AA, plus underlined links in ' +
      'text blocks.',
    metric:
      'Completion rate for patients using larger text or high-contrast settings; axe serious violations (16 → 0).',
    cleanup:
      'EXIT CONDITION MET 2026-09-16 (Q-P15): the owner accepted the visible colour ' +
      'change, so this flag now DEFAULTS ON and `packages/ui` uses the accessible tokens ' +
      'for every white-on-primary surface in all three apps (KI-03 closed). What is left ' +
      'is deletion: after stage 3 of `Docs/_parity/improvements/README.md` §3, delete the ' +
      "flag, the `data-a11y` attribute and the `[data-a11y='on']` block in booking.css, " +
      'and let the accessible values replace the live ones they shadow in theme.css. The ' +
      'parity harness then loses its `?flags=none` baseline and FR-BOOK-01 is retired ' +
      'with it.',
  },
  'funnel.stepsUnified': {
    key: 'funnel.stepsUnified',
    summary:
      'One four-step indicator (Book · Insurance · Payment · Paperwork) on every page, ' +
      'including /payment, which live shows a different three-step bar.',
    metric:
      'Abandonment on /payment — the live bar tells the patient they are on step 2 of 3 after being on step 1 of 4.',
    cleanup:
      'Delete the flag, `PAYMENT_STEPS` and the funnel-local `FunnelSteps` copy; the ' +
      'design-system `StepIndicator` becomes the only step bar.',
  },
  'funnel.fewerSteps': {
    key: 'funnel.fewerSteps',
    summary:
      'The chosen slot stays pinned above the details fields on /book, so the slot and ' +
      'the name/email/phone the patient is typing are on one screen.',
    metric: 'Completion of /book: details entered ÷ slots chosen.',
    cleanup:
      'Delete the flag and keep the pinned summary once it wins; the `.book-together` ' +
      'wrapper becomes the only /book layout.',
  },
  'funnel.timezoneClarity': {
    key: 'funnel.timezoneClarity',
    summary:
      'Clinic time shown on every slot when the zones differ, a "Show times in" switch, ' +
      'and the timezone note at the funnel radius.',
    metric: 'No-show rate for out-of-zone patients, and slot re-picks per booking.',
    cleanup:
      'Delete the flag once the no-show rate for out-of-zone patients is measured over ' +
      'one full month; the switch and the dual labels stay.',
  },
  'funnel.mobilePolish': {
    key: 'funnel.mobilePolish',
    summary:
      '44px tap targets for chips and slots, a primary button pinned above the ' +
      'keyboard-safe area, and safe-area insets. Nothing changes at 768 and above.',
    metric: 'Mobile completion rate (the funnel is majority mobile) and mis-taps per slot chosen.',
    cleanup:
      'Delete the flag and fold the rules into the base `@media (max-width: 767px)` block ' +
      'once mobile completion is measured for one month.',
  },
  'funnel.resumePrompt': {
    key: 'funnel.resumePrompt',
    summary:
      '"Continue where you left off?" prompt and its empty state, driven by the ' +
      'in-memory draft until server drafts land.',
    metric: 'Recovered sessions ÷ abandoned drafts (FR-BOOK-10).',
    cleanup:
      'M5.2 replaces the in-memory source with the server draft; the flag is deleted at ' +
      'the same time, because a prompt over a draft that does not survive a reload is not ' +
      'worth shipping on its own.',
  },
  'funnel.loadingSkeletons': {
    key: 'funnel.loadingSkeletons',
    summary:
      'Skeleton date chips and slot tiles instead of the "Checking live availability…" ' +
      'line, with reduced-motion support.',
    metric: 'Drop-off during slot load once availability is a real network call (M5.7).',
    cleanup:
      'Delete the flag once M5.7 makes availability a real request and the skeleton is ' +
      'the only loading state; the plain-text states stay for the error and empty cases.',
  },
  'funnel.insuranceCard': {
    key: 'funnel.insuranceCard',
    summary:
      'A photo of the insurance card, front and back, on /insurance — with a progress ' +
      'bar, the scanner wait and a thumbnail of what was uploaded. The live page has no ' +
      'upload of any kind.',
    metric:
      'Coverage checks a staff member has to finish by hand: a legible card is what turns ' +
      'an `unavailable` eligibility answer into a five-minute job rather than a phone call.',
    cleanup:
      'Delete the flag and keep the block once the clinic confirms the cards are worth ' +
      'the two extra controls; `InsuranceCardUpload` then renders unconditionally. It ' +
      'also needs `DELETE /public/files/:id` before "Remove" can mean what it says ' +
      '(BOOKING_FUNNEL_WIRING.md §8).',
  },
  'funnel.smsConsent': {
    key: 'funnel.smsConsent',
    summary:
      'A TCPA opt-in under the phone number on /book, so a patient who wants texted ' +
      'reminders can say so where they give the number. The live page collects the ' +
      'number and asks nothing.',
    metric:
      'Share of bookings with an SMS opt-in, and the no-show rate for patients who have ' +
      'one — an SMS reminder cannot be sent at all without this consent ' +
      '(FR-NOTIF-08, `PreferencesService.allowsSms`).',
    cleanup:
      'Delete the flag once the clinic publishes its own `sms` wording under ' +
      "`legal.consents`: the generic consent block then renders it and this branch's " +
      'platform fallback (`lib/consent-copy.ts`) has nothing left to do. Until then it ' +
      'stays flagged, because a consent the platform wrote is a consent the clinic has ' +
      'not read.',
  },
  'funnel.marketingConsent': {
    key: 'funnel.marketingConsent',
    summary:
      'The 45 CFR 164.508 marketing authorization on /insurance — unchecked by default, ' +
      'saying what is shared, that it is optional and that it can be revoked. The live ' +
      'page has no such box.',
    metric:
      'Authorizations captured per booking (FR-CRM-03): the CRM sync sends marketing ' +
      'fields for a patient who has one and the operational set only for a patient who ' +
      'does not (FR-CRM-04), so this number is the whole size of the clinic’s ' +
      'addressable list.',
    cleanup:
      'Delete the flag when HeyAllergy counsel answers Q-P19 and the wording is ' +
      'published as a tenant setting; the box then renders from `legal.consents` like ' +
      'every other consent and the placeholder in `lib/consent-copy.ts` goes with it.',
  },
  'funnel.insuranceType': {
    key: 'funnel.insuranceType',
    summary:
      'M15 scheduling rules (ADR-032): a required "What kind of plan is it?" radio above ' +
      'the insurer picker on /insurance, the pre-verification decision ' +
      '(`POST /public/eligibility/decision`) and the post-verification one honoured — a ' +
      '"contact the office" panel, or the self-pay price with a one-line notice. The live ' +
      'page asks no such question.',
    metric:
      'Bookings that reach confirm only to be refused (`409 SCHEDULING_BLOCKED`) should be ' +
      'zero; `booking_blocked` (server-side) ÷ insurance steps started is the share the ' +
      "clinic's rules stop, and the review queue's `scheduling_blocked` tasks are the " +
      'call-backs it creates.',
    cleanup:
      'Not a rollout flag in the FR-BOOK-02 sense: it is ON exactly for a tenant with a ' +
      'published scheduling ruleset, because the question only matters when a rule reads ' +
      'the answer. The API puts `funnel.insuranceType` in `features.ui` for such a tenant ' +
      '(BOOKING_FUNNEL_WIRING.md §3.11). Delete the flag, and render the radio ' +
      'unconditionally, once FR-BOOK-01 (the parity bar) is retired — until then the ' +
      'flags=none baseline must not grow a field.',
  },
};

/**
 * Parity first — every flag is off (ADR-008), with ONE exception.
 *
 * `funnel.a11yContrast` defaults **ON** as of **2026-09-16** (Q-P15, closing KI-03):
 * Roman accepted the visible colour change on the funnel's buttons, links and steps, so
 * the accessible palette is what a patient gets. Stage 4 of the rollout plan
 * (`Docs/_parity/improvements/README.md` §3) still has to run before the flag and its
 * `[data-a11y='on']` branch can be deleted, which is why this is a default and not yet a
 * removal.
 *
 * THE PARITY BASELINE IS NOW AN EXPLICIT QUERY. `apps/patient/parity/compare.mjs` drives
 * every capture with `?flags=none`, so the harness still measures the flags-off build
 * against the live site and FR-BOOK-01 is unchanged. The shipped default differs from
 * that baseline by this one flag and nothing else
 * (`Docs/BOOKING_FUNNEL_WIRING.md` §"Parity baseline").
 */
export const FLAG_DEFAULTS: Readonly<FunnelFlags> = Object.freeze({
  ...(Object.fromEntries(FUNNEL_FLAG_NAMES.map((name) => [name, false])) as FunnelFlags),
  a11yContrast: true,
});

/** Is this string one of the registry's keys? Anything else is ignored, never an error. */
export function isFlagKey(value: string): value is FlagKey {
  return Object.prototype.hasOwnProperty.call(FLAG_REGISTRY, value);
}

/* ------------------------------------------------------------------ *
 * Layer 2 — the tenant's feature set (M5.7 seam)
 * ------------------------------------------------------------------ */

/**
 * The shape `GET /public/tenant` will return under `features` (FR-PLAT-03).
 *
 * TODO(M5.7): the bootstrap that fetches the tenant calls `setTenantFeatures(...)` with
 * this object and nothing else in the app changes — every call site already reads the
 * resolved answer through `useFlags()` / `readFlags()`.
 */
export interface TenantFeatures {
  /** Per-tenant flag values. Unknown keys are ignored, so an old bundle stays safe. */
  flags?: Partial<Record<FlagKey, boolean>>;
  /**
   * Whether `?flags=` on the URL may override this tenant's values. Absent means no:
   * once a tenant is loaded, overrides are opt-in.
   */
  flagOverrides?: boolean;
}

/* ------------------------------------------------------------------ *
 * Layer 3 — `?flags=` parsing
 * ------------------------------------------------------------------ */

/** The largest `?delay=` the evidence seam will honour. */
const MAX_EVIDENCE_DELAY_MS = 5000;

/** One query parameter, or null — including when there is no parseable URL at all. */
function readParam(search: string, name: string): string | null {
  try {
    return new URLSearchParams(search).get(name);
  } catch {
    return null;
  }
}

/**
 * Parse `?flags=funnel.fewerSteps,-funnel.fontFix` into an override map.
 *
 * - a bare key turns the flag ON;
 * - a `-` prefix turns it OFF (so a tenant default can be disabled for a screenshot);
 * - `all` turns every flag on and `none` turns every flag off;
 * - anything else — a typo, a removed flag, a key from a newer bundle — is IGNORED, so
 *   a stale link can never break the page.
 *
 * Exported for the test suite and for the parity harness's documentation.
 */
export function parseFlagOverrides(search: string): Partial<Record<FlagKey, boolean>> {
  const out: Partial<Record<FlagKey, boolean>> = {};
  const raw = readParam(search, 'flags');
  if (raw === null || raw.trim() === '') return out;

  for (const token of raw.split(',')) {
    const trimmed = token.trim();
    if (trimmed === '') continue;

    const off = trimmed.startsWith('-');
    const name = off ? trimmed.slice(1) : trimmed;

    if (name === 'all' || name === 'none') {
      const value = name === 'all' ? !off : off;
      for (const key of Object.keys(FLAG_REGISTRY) as FlagKey[]) out[key] = value;
      continue;
    }
    if (!isFlagKey(name)) continue;
    out[name] = !off;
  }
  return out;
}

/**
 * `?delay=800` — an artificial wait in front of the demo date and slot loaders, so the
 * skeleton states of `funnel.loadingSkeletons` can be screenshotted before M5.7 makes
 * availability a real request. Clamped, and honoured on exactly the same terms as
 * `?flags=`.
 *
 * TODO(M5.7): delete with the demo loaders.
 */
export function parseEvidenceDelayMs(search: string): number {
  const raw = readParam(search, 'delay');
  const value = Number(raw);
  if (raw === null || !Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), MAX_EVIDENCE_DELAY_MS);
}

/* ------------------------------------------------------------------ *
 * Resolution and the store
 * ------------------------------------------------------------------ */

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

let tenantFeatures: TenantFeatures | null = null;
let overrides: Partial<Record<FlagKey, boolean>> = parseFlagOverrides(currentSearch());
let evidenceDelayMs = parseEvidenceDelayMs(currentSearch());

/**
 * Overrides are allowed while there is no tenant — the state M5 ships in, and the state
 * the parity harness drives the built app in — and, once M5.7 loads one, only when that
 * tenant opts in with `flagOverrides: true`. A dev tenant fixture should set it; a
 * production tenant should not, and then a pasted `?flags=` link does nothing.
 *
 * There is deliberately no `import.meta.env.DEV` escape hatch: a rule that behaves one
 * way in the dev server and another way in the bundle the harness photographs is a rule
 * nobody can reason about.
 */
function overridesAllowed(): boolean {
  if (tenantFeatures === null) return true;
  return tenantFeatures.flagOverrides === true;
}

function resolve(): Flags {
  const funnel: FunnelFlags = { ...FLAG_DEFAULTS };

  const fromTenant = tenantFeatures?.flags ?? {};
  for (const [key, value] of Object.entries(fromTenant)) {
    if (isFlagKey(key) && typeof value === 'boolean') {
      funnel[key.slice('funnel.'.length) as FunnelFlagName] = value;
    }
  }

  if (overridesAllowed()) {
    for (const [key, value] of Object.entries(overrides)) {
      if (isFlagKey(key) && typeof value === 'boolean') {
        funnel[key.slice('funnel.'.length) as FunnelFlagName] = value;
      }
    }
  }

  return { funnel };
}

let snapshot: Flags = resolve();
const listeners = new Set<() => void>();

function republish(): void {
  snapshot = resolve();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Flags {
  return snapshot;
}

/** The resolved answer, for the call sites that are not components. */
export function readFlags(): Flags {
  return snapshot;
}

/** The resolved answer, with a re-render when the tenant's features arrive. */
export function useFlags(): Flags {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** How long the demo loaders should wait, per `?delay=`. Zero in every normal build. */
export function readEvidenceDelayMs(): number {
  return overridesAllowed() ? evidenceDelayMs : 0;
}

/**
 * The M5.7 seam. Call once, after `GET /public/tenant` resolves; pass `null` to go back
 * to the defaults. Every flag reader re-renders.
 */
export function setTenantFeatures(features: TenantFeatures | null): void {
  tenantFeatures = features;
  republish();
}

/**
 * Re-read `?flags=` and `?delay=` from the current URL. The router never reloads the
 * page, so this exists for the (dev-only) case of editing the query string in place, and
 * for tests.
 */
export function refreshFlagOverrides(search: string = currentSearch()): void {
  overrides = parseFlagOverrides(search);
  evidenceDelayMs = parseEvidenceDelayMs(search);
  republish();
}

/**
 * Test seam: force a set of flags without going through a URL. Production code never
 * calls this — `vitest.setup.ts` resets it after every test.
 */
export function setFlagOverridesForTest(next: Partial<Record<FlagKey, boolean>>): void {
  overrides = { ...next };
  republish();
}

/** Test seam: back to the shipped defaults — no tenant and no overrides. */
export function resetFlagsForTest(): void {
  tenantFeatures = null;
  overrides = {};
  evidenceDelayMs = 0;
  republish();
}
